import { Controller } from "@hotwired/stimulus"

// Pins the chat drawer's message list to the bottom while content
// streams in, but stays put if the user has scrolled up to read.
//
// Key idea: stickiness is decided from SCROLL events, not from the
// post-mutation distance. The previous version checked
// `scrollHeight - scrollTop - clientHeight <= threshold` inside the
// MutationObserver, but a single chunk can grow scrollHeight by more
// than the threshold in one tick — which made the controller falsely
// conclude the user had scrolled up, and stop following. Tracking
// stickiness via the scroll listener (where the user's intent shows
// up) and only consulting the flag on mutation avoids that.
export default class extends Controller {
  static values = { threshold: { type: Number, default: 80 } }

  connect() {
    // Start pinned — we open the drawer at the bottom.
    this.stickToBottom = true

    this.onScroll = () => this.updateStickiness()
    this.element.addEventListener("scroll", this.onScroll, { passive: true })

    // Observe the scroll area for any DOM growth: new messages appended by
    // Message's after_create_commit broadcast (childList), streamed text
    // chunks appended into `message_<id>_content` divs by
    // `Message#broadcast_append_chunk` (childList + characterData), the
    // rendered markdown that follows them, and the status line under
    // #messages ("Thinking…").
    this.observer = new MutationObserver(() => this.handleMutation())
    this.observer.observe(this.element, {
      childList: true,
      subtree: true,
      characterData: true,
    })

    this.scrollToBottom()
  }

  disconnect() {
    this.observer?.disconnect()
    this.element.removeEventListener("scroll", this.onScroll)
  }

  updateStickiness() {
    const distance =
      this.element.scrollHeight - this.element.scrollTop - this.element.clientHeight
    this.stickToBottom = distance <= this.thresholdValue
  }

  handleMutation() {
    // Scroll synchronously: MutationObserver callbacks run before the next
    // paint and reading scrollHeight forces layout, so the new content is
    // never painted overflowing for a frame (which read as jitter while a
    // reply streamed in line by line).
    if (this.stickToBottom) this.element.scrollTop = this.element.scrollHeight
  }

  scrollToBottom() {
    // rAF runs after layout for the mutation that triggered us, so
    // scrollHeight reflects the chunk we just appended.
    requestAnimationFrame(() => {
      this.element.scrollTop = this.element.scrollHeight
    })
  }
}
